import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Grid } from "@mui/material";

const CompanyInformationDialog = ({ open, handleClose, innovationId, startupData }) => {

  const [formData, setFormData] = useState({
    country_id: "",
    city: "",
    address: "",
    accountType: "",
    name: "",
    founders: "",
    phone: "",
    email: "",
    linkedin_url: "",
    website: "",
    founding_year: "",
    total_funding: "",
    companyStatus: "",
    company_type_id: "",
    company_stage_id: "",
    business_model_id: "",
    otherCountries: [],
    countryPresence: [],
    integration_timing: "",
    //numberOfEmployees: "",
    revenue: "",
    platforms: "",
    clients: "",
  });

  const [errors, setErrors] = useState({});
  const [dropdownValues, setDropdownValues] = useState({
    countries: [],
    companyTypes: [],
    companyStages: [],
    businessModels: [],
    targetMarkets: [],
  });

  // Fetch dropdown values including target markets
  useEffect(() => {
    const fetchDropdownValues = async () => {
      const token = localStorage.getItem("token");
      try {
        const [filtersRes, countriesRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/values/innovation-filters?admin=false`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${process.env.REACT_APP_API_URL}/values/countries`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        const filtersData = await filtersRes.json();
        const countriesData = await countriesRes.json();

        setDropdownValues((prev) => ({
          ...prev,
          countries: countriesData.data || [],
          companyTypes: filtersData.data.company_types || [],
          companyStages: filtersData.data.stages || [],
          businessModels: filtersData.data.business_models || [],
          targetMarkets: filtersData.data.target_markets || [],
        }));
      } catch (error) {
        console.error("Error fetching dropdown values:", error);
      }
    };

    fetchDropdownValues();
  }, []);


  // Populate formData only after dropdownValues are available
  useEffect(() => {
    if (startupData && dropdownValues.targetMarkets.length > 0 && dropdownValues.countries.length > 0) {
      setFormData((prev) => ({
        ...prev,
        country_id: startupData.country?.id?.toString() || "",
        city: startupData.city || "",
        address: startupData.address || "",
        name: startupData.name || "",
        founders: startupData.founders ? startupData.founders.replaceAll(";", "") : "",
        phone: startupData.phone ? startupData.phone.replaceAll(";", "") : "",
        email: startupData.emails ? startupData.emails.replaceAll(";", "") : "",
        linkedin_url: startupData.linkedin_url || "",
        website: startupData.website || "",
        founding_year: startupData.founding_year || "",
        total_funding: startupData.total_funding || "",
        company_type_id: startupData.company_type?.id?.toString() || "",
        company_stage_id: startupData.company_stage?.id?.toString() || "",
        business_model_id: startupData.business_model?.id?.toString() || "",
        integration_timing: startupData.integration_timing || "",
        revenue: startupData.revenue || "",
        platforms: startupData.platforms ? startupData.platforms.replaceAll(";", "") : "",
        clients: startupData.clients ? startupData.clients.replaceAll(";", "") : "",
        otherCountries: Array.isArray(startupData.target_markets)
          ? startupData.target_markets
              .map(m => (m?.id != null ? m.id.toString() : null))
              .filter(id => id !== null)
          : [],
        countryPresence: Array.isArray(startupData.country_presence)
          ? startupData.country_presence.map(c => c.id.toString())  // ⬅️ AGGIUNGI QUESTO
          : [],

        }));        
    }
  }, [startupData, dropdownValues]);

  const handleInputChange = (event) => {
    let { name, value } = event.target;

    // Rimuove i separatori per salvarli come numeri raw
    if (["total_funding", "revenue"].includes(name)) {
      value = value.replace(/,/g, '');
    }

  
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const requiredFields = [
    "name",
    "website",
    "linkedin_url",
    "country_id",
    "founding_year",
    "company_type_id",
    "business_model_id",
    "company_stage_id",
    "otherCountries",
    "founders",
  ];

  const handleUpdate = async () => {
    const token = localStorage.getItem("token");

    let newErrors = {};
    requiredFields.forEach((field) => {
      if (!formData[field] || (typeof formData[field] === "string" && formData[field].trim() === "")) {
        newErrors[field] = "This field is required";
      }
    });

    // Validazione: Clients e Platforms devono usare solo virgole
    const commaSeparatedPattern = /^([^;,|]+)(,\s*[^;,|]+)*$/;

    if (formData.clients && !commaSeparatedPattern.test(formData.clients.trim())) {
      newErrors.clients = "Use only commas to separate clients (e.g. Google, Microsoft)";
    }

    if (formData.platforms && !commaSeparatedPattern.test(formData.platforms.trim())) {
      newErrors.platforms = "Use only commas to separate platforms (e.g. AWS, Azure)";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Creazione del payload senza campi vuoti
    let payload = {};
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== null && formData[key] !== undefined && formData[key] !== "") {
        payload[key] = formData[key];
      }
    });

    // Aggiunta dei `;` nei campi richiesti
    if (formData.founders) payload.founders = `;${formData.founders};`;
    if (formData.phone) payload.phone = `;${formData.phone};`;
    if (formData.email) {
      payload.emails = `;${formData.email};`;
      delete payload.email;
    }
    if (formData.clients) payload.clients = formData.clients;
    if (formData.platforms) payload.platforms = formData.platforms;

    // Gestione corretta di `target_markets`, `tags` e `categories`
    if (Array.isArray(formData.otherCountries) && formData.otherCountries.length > 0) {
      payload.target_markets = formData.otherCountries
        .map(value => (value === "null" ? null : Number(value))) // Converte "null" in null e numeri in Number
        .filter(id => id !== null && id !== undefined && id !== "" && !isNaN(id)); // Filtra NaN e valori non validi
    }

    delete payload.otherCountries;

    if (Array.isArray(formData.countryPresence) && formData.countryPresence.length > 0) {
      payload.country_presence = formData.countryPresence
        .map(value => (value === "null" ? null : Number(value)))
        .filter(id => id !== null && id !== undefined && id !== "" && !isNaN(id));
    }

    delete payload.countryPresence;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/innovations/${innovationId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        alert("Data updated successfully!");
        handleClose();
      } else {
        alert("Failed to update data.");
      }
    } catch (error) {
      console.error("Error updating data:", error);
      alert("An error occurred.");
    }
  };
  
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: "bold", backgroundColor: "#191919", color: "white", textAlign: "center" }}>
        Company Information
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: "#191919", color: "white", padding: "24px" }}>
        <Grid container spacing={3}>
  
          {/* Company Name (Required) */}
          <Grid item xs={12} md={6}>
            <TextField
              name="name"
              label="Company Name"
              fullWidth
              variant="outlined"
              value={formData.name}
              onChange={handleInputChange}
              sx={textFieldStyle}
              required
              error={Boolean(errors.name)}
              helperText={errors.name || ""}
            />
          </Grid>
  
          {/* Website (Required) */}
          <Grid item xs={12} md={6}>
            <TextField
              name="website"
              label="Website"
              fullWidth
              variant="outlined"
              value={formData.website}
              onChange={handleInputChange}
              sx={textFieldStyle}
              required
              error={Boolean(errors.website)}
              helperText={errors.website || ""}
            />
          </Grid>
  
          {/* linkedin_url URL (Required) */}
          <Grid item xs={12} md={6}>
            <TextField
              name="linkedin_url"
              label="linkedin_url URL"
              fullWidth
              variant="outlined"
              value={formData.linkedin_url}
              onChange={handleInputChange}
              sx={textFieldStyle}
              required
              error={Boolean(errors.linkedin_url)}
              helperText={errors.linkedin_url || ""}
            />
          </Grid>
  
          {/* country_id (Required) */}
          <Grid item xs={12} md={6}>
            <TextField
              name="country_id"
              label="Country"
              select
              fullWidth
              variant="outlined"
              value={formData.country_id}
              onChange={handleInputChange}
              sx={textFieldStyle}
              required
              error={Boolean(errors.country_id)}
              helperText={errors.country_id || ""}
            >
              {dropdownValues.countries.map((item) => (
                <MenuItem key={item.id} value={item.id.toString()}>{item.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
  
          {/* Founding Year (Required) */}
          <Grid item xs={12} md={6}>
            <TextField
              name="founding_year"
              label="Founding Year"
              fullWidth
              variant="outlined"
              value={formData.founding_year}
              onChange={handleInputChange}
              sx={textFieldStyle}
              required
              error={Boolean(errors.founding_year)}
              helperText={errors.founding_year || ""}
            />
          </Grid>

          {/* Total Founding (Required) */}
          <Grid item xs={12} md={6}>
            <TextField
              name="total_funding"
              label="Total Founding"
              fullWidth
              variant="outlined"
              value={formData.total_funding ? Number(formData.total_funding).toLocaleString() : ""}
              onBlur={(e) => {
  const raw = e.target.value.replace(/,/g, '');
  if (!isNaN(raw)) {
    e.target.value = Number(raw).toLocaleString();
  }
}}
              onChange={handleInputChange}
              sx={textFieldStyle}
              placeholder="e.g. 1000000"
              error={Boolean(errors.total_funding)}
              helperText={errors.total_funding || ""}
            />
          </Grid>
  
          {/* Company Type (Required) */}
          <Grid item xs={12} md={6}>
            <TextField
              name="company_type_id"
              label="Company Type"
              select
              fullWidth
              variant="outlined"
              value={formData.company_type_id}
              onChange={handleInputChange}
              sx={textFieldStyle}
              required
              error={Boolean(errors.company_type_id)}
              helperText={errors.company_type_id || ""}
            >
              {dropdownValues.companyTypes.map((item) => (
                <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
  
          {/* Business Model (Required) */}
          <Grid item xs={12} md={6}>
            <TextField
              name="business_model_id"
              label="Business Model"
              select
              fullWidth
              variant="outlined"
              value={formData.business_model_id}
              onChange={handleInputChange}
              sx={textFieldStyle}
              required
              error={Boolean(errors.business_model_id)}
              helperText={errors.business_model_id || ""}
            >
              {dropdownValues.businessModels.map((item) => (
                <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
  
          {/* Company Stage (Required) */}
          <Grid item xs={12} md={6}>
            <TextField
              name="company_stage_id"
              label="Company Stage"
              select
              fullWidth
              variant="outlined"
              value={formData.company_stage_id}
              onChange={handleInputChange}
              sx={textFieldStyle}
              required
              error={Boolean(errors.company_stage_id)}
              helperText={errors.company_stage_id || ""}
            >
              {dropdownValues.companyStages.map((item) => (
                <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
  
          {/* Target Markets (Required) */}
          <Grid item xs={12} md={6}>
          <TextField
              name="otherCountries"
              label="Target Markets"
              select
              fullWidth
              variant="outlined"
              value={formData.otherCountries}
              onChange={handleInputChange}
              sx={textFieldStyle}
              required
              error={Boolean(errors.otherCountries)}
              helperText={errors.otherCountries || ""}
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  // --- Ecco dove aggiungi il console.log
                  //console.log("Selected IDs:", selected);
            
                  const selectedItems = dropdownValues.targetMarkets
                    .filter((item) => selected.includes(item.id));
            
                  //console.log("Selected Items after filter:", selectedItems);
                  
                  return selectedItems.map((item) => item.name).join(", ");
                },
              }}
            >
              {dropdownValues.targetMarkets.map((item) => (                
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>

          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              name="countryPresence"
              label="Presence in Other Countries"
              select
              fullWidth
              variant="outlined"
              value={formData.countryPresence}
              onChange={handleInputChange}
              sx={textFieldStyle}
              SelectProps={{
                multiple: true,
                MenuProps: {
                  PaperProps: {
                    sx: {
                      maxHeight: 200, // altezza max dropdown
                      mt: 1,           // distanza dal campo
                      bgcolor: "#191919",
                      color: "#fff"
                    },
                  },
                  anchorOrigin: {
                    vertical: "bottom", // dropdown parte da sotto
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                },
                 renderValue: (selected) => {
                  const selectedItems = dropdownValues.countries.filter((item) =>
                    selected.includes(item.id.toString())
                  );
                  return selectedItems.map((item) => item.name).join(", ");
                }
              }}
            >
              {dropdownValues.countries.map((item) => (
                <MenuItem key={item.id} value={item.id.toString()}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
  
          {/* Founders (Required) */}
          <Grid item xs={12} md={6}>
            <TextField
              name="founders"
              label="Founders"
              fullWidth
              variant="outlined"
              value={formData.founders}
              onChange={handleInputChange}
              sx={textFieldStyle}
              required
              error={Boolean(errors.founders)}
              helperText={errors.founders || ""}
            />
          </Grid>

          {/* Address */}
          <Grid item xs={12} md={6}>
            <TextField
              name="address"
              label="Address"
              fullWidth
              variant="outlined"
              value={formData.address}
              onChange={handleInputChange}
              sx={textFieldStyle}
            />
          </Grid>
  
          {/* City (Opzionale) */}
          <Grid item xs={12} md={6}>
            <TextField
              name="city"
              label="City"
              fullWidth
              variant="outlined"
              value={formData.city}
              onChange={handleInputChange}
              sx={textFieldStyle}
            />
          </Grid>
  
          {/* Phone (Opzionale) */}
          <Grid item xs={12} md={6}>
            <TextField
              name="phone"
              label="Phone"
              fullWidth
              variant="outlined"
              value={formData.phone}
              onChange={handleInputChange}
              sx={textFieldStyle}
            />
          </Grid>
  
          {/* Email (Opzionale) */}
          <Grid item xs={12} md={6}>
            <TextField
              name="email"
              label="Email"
              fullWidth
              variant="outlined"
              value={formData.email}
              onChange={handleInputChange}
              sx={textFieldStyle}
            />
          </Grid>          
  
          {/* Revenue (Opzionale) */}
          <Grid item xs={12} md={6}>
            <TextField
              name="revenue"
              label="Revenue"
              fullWidth
              variant="outlined"
              value={formData.revenue ? Number(formData.revenue).toLocaleString() : ""}
              onChange={handleInputChange}
              sx={textFieldStyle}
              onBlur={(e) => {
              const raw = e.target.value.replace(/,/g, '');
              if (!isNaN(raw)) {
                e.target.value = Number(raw).toLocaleString();
              }
            }}
            />            
          </Grid>

          {/* Integration Timing */}
          <Grid item xs={12} md={6}>
            <TextField
              name="integration_timing"
              label="Integration Timing"
              fullWidth
              variant="outlined"
              value={formData.integration_timing}
              onChange={handleInputChange}
              sx={textFieldStyle}
              placeholder="e.g. 1 week, 2 months"
            />
          </Grid>

          {/* Clients */}
          <Grid item xs={12} md={6}>
          <TextField
              name="clients"
              label="Clients"
              fullWidth
              variant="outlined"
              value={formData.clients}
              onChange={handleInputChange}
              sx={textFieldStyle}
              placeholder="e.g. Google, Microsoft"
              error={Boolean(errors.clients)}
              helperText={errors.clients || ""}
            />           
          </Grid>

          {/* Platforms (Opzionale) */}
          <Grid item xs={12} md={6}>
          <TextField
              name="platforms"
              label="Platforms"
              fullWidth
              variant="outlined"
              value={formData.platforms}
              onChange={handleInputChange}
              sx={textFieldStyle}
              placeholder="e.g. Google, Microsoft"
              error={Boolean(errors.platforms)}
              helperText={errors.platforms || ""}

            />           
          </Grid>

          
  
        </Grid>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#191919", padding: "16px" }}>
        <Button onClick={handleClose} sx={{ color: "white" }}>
          Cancel
        </Button>
        <Button onClick={handleUpdate} sx={{ backgroundColor: "rgb(var(--global-color-primary))", color: "black", fontWeight: "bold" }}>
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
  
  
};

const textFieldStyle = {
  marginBottom: "16px",
  "& .MuiInputBase-root": { color: "white" },
};

export default CompanyInformationDialog;
